// TemplatePublishModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useProgress } from 'context/ProgressContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Hooks
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './TemplatePublishModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import TextInput from 'components/textinput/TextInput';
import { toast } from 'react-toastify';

// Services
import { publishTemplateSnapshot } from 'services/templates/TemplateRuntimeService';

const TemplatePublishModal = React.memo(({
  isOpen,
  setOpen,
  workspace,
}) => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const { showProgress, hideProgress } = useProgress();
  const { user } = useUser();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setTitle(`${workspace?.title || 'Workspace'} Template`);
    setDescription('');
    setErrorMessage('');
  }, [isOpen, workspace?.title]);

  const canSave = useMemo(() => {
    return Boolean(!saving && workspace?.key && title.trim());
  }, [saving, workspace?.key, title]);

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
  };

  const handleSave = async () => {
    if (!canSave) return;

    setSaving(true);
    setErrorMessage('');

    try {
      showProgress && showProgress('Publishing template...');
      await publishTemplateSnapshot({
        workspaceKey: workspace.key,
        title: title.trim(),
        description: description.trim(),
        authorKey: user?.key || null,
        authorEmail: user?.data_profile?.email || user?.email || '',
      });
      toast.success('Template published successfully.');
      setOpen(false);
    } catch (error) {
      console.error('[TemplatePublishModal] publish template error:', error);
      setErrorMessage(error?.message || 'Failed to publish template.');
    } finally {
      setSaving(false);
      hideProgress && hideProgress();
    }
  };

  return (
    <Modal
      title="Publish Template"
      isOpen={isOpen}
      onClose={handleClose}
      width={isMobile ? '100%' : 420}
      height="auto"
    >
      <div className="template-publish-modal-container">
        <div className="template-publish-modal-section">
          <div className="template-publish-modal-label" style={{ color: theme.foregroundMuted }}>
            Template Title
          </div>
          <TextInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={50}
            autoFocus
            autoSelect
            showCount
          />
        </div>

        <div className="template-publish-modal-section">
          <div className="template-publish-modal-label" style={{ color: theme.foregroundMuted }}>
            Description (Optional)
          </div>
          <TextInput
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={300}
            showCount
            multiline
            expandable
            placeholder="Brief description..."
          />
        </div>

        {errorMessage && (
          <div className="template-publish-modal-error" style={{ color: theme.warning || theme.red || '#ff7f7f' }}>
            {errorMessage}
          </div>
        )}
      </div>

      <ModalButtons>
        <Button
          label={saving ? 'Publishing...' : 'Publish Template'}
          onClick={handleSave}
          enabled={canSave}
        />
      </ModalButtons>
    </Modal>
  );
});

TemplatePublishModal.displayName = 'TemplatePublishModal';

export default TemplatePublishModal;